import { CheckCircle, Clock, Loader2, Scale, ThumbsDown, ThumbsUp, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { createReview, getReviews } from '@/services/reviewService';

import type { ReviewDecision, ReviewInfo, SubmissionStage } from '@/types/submission.types';

const REASON_CODE_KEYS = [
  'license_expired',
  'incomplete_docs',
  'insufficient_experience',
  'failed_background',
  'availability_mismatch',
  'overqualified',
  'pending_license_renewal',
  'needs_additional_training',
  'strong_candidate',
  'other',
] as const;

const TERMINAL_STAGES: SubmissionStage[] = ['approved', 'rejected'];

const DECISION_STYLES: Record<ReviewDecision, { bg: string; hover: string; icon: typeof ThumbsUp }> = {
  YES: { bg: 'bg-emerald-600', hover: 'hover:bg-emerald-700', icon: ThumbsUp },
  NO: { bg: 'bg-red-600', hover: 'hover:bg-red-700', icon: ThumbsDown },
  WAITLIST: { bg: 'bg-amber-500', hover: 'hover:bg-amber-600', icon: Clock },
};

const DECISION_BADGE_STYLES: Record<string, string> = {
  YES: 'bg-emerald-100 text-emerald-800',
  NO: 'bg-red-100 text-red-800',
  WAITLIST: 'bg-amber-100 text-amber-800',
};

interface DecisionPanelProps {
  applicationId: string;
  stage: SubmissionStage;
  onDecisionMade: () => void;
}

export function DecisionPanel({ applicationId, stage, onDecisionMade }: DecisionPanelProps) {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<ReviewInfo[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState<ReviewDecision | null>(null);
  const [reasonCode, setReasonCode] = useState('');
  const [notes, setNotes] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isTerminal = TERMINAL_STAGES.includes(stage);
  const requiresReason = selectedDecision === 'NO' || selectedDecision === 'WAITLIST';

  // Load review history
  useEffect(() => {
    setIsLoadingHistory(true);
    void getReviews(applicationId)
      .then(setReviews)
      .finally(() => setIsLoadingHistory(false));
  }, [applicationId]);

  const handleSelectDecision = useCallback((decision: ReviewDecision) => {
    setSelectedDecision(decision);
    setReasonCode('');
    setNotes('');
    setIsConfirming(false);
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedDecision(null);
    setReasonCode('');
    setNotes('');
    setIsConfirming(false);
  }, []);

  const handleConfirmClick = useCallback(() => {
    if (requiresReason && !reasonCode) return;
    setIsConfirming(true);
  }, [requiresReason, reasonCode]);

  const handleSubmit = useCallback(async () => {
    if (!selectedDecision) return;
    if (requiresReason && !reasonCode) return;

    setIsSubmitting(true);
    try {
      const review = await createReview({
        applicationId,
        decision: selectedDecision,
        reasonCode: reasonCode || undefined,
        notes: notes || undefined,
      });
      setReviews((prev) => [review, ...prev]);
      setSelectedDecision(null);
      setReasonCode('');
      setNotes('');
      setIsConfirming(false);
      toast.success(t('submissionDetail.decision.success'));
      onDecisionMade();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('submissionDetail.decision.error');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [applicationId, selectedDecision, reasonCode, notes, requiresReason, onDecisionMade, t]);

  const confirmMessage =
    selectedDecision === 'YES'
      ? t('submissionDetail.decision.confirmApprove')
      : selectedDecision === 'NO'
        ? t('submissionDetail.decision.confirmReject')
        : t('submissionDetail.decision.confirmWaitlist');

  return (
    <div className='border-t border-gray-200 pt-4 mt-4'>
      <h3 className='text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-3'>
        <Scale size={16} />
        {t('submissionDetail.decision.title')}
      </h3>

      {/* Decision buttons */}
      {!isTerminal && !selectedDecision ? (
        <div className='flex gap-2'>
          {(['YES', 'NO', 'WAITLIST'] as const).map((decision) => {
            const style = DECISION_STYLES[decision];
            const Icon = style.icon;
            const label = t(`submissionDetail.decision.${decision.toLowerCase()}`);
            return (
              <button
                key={decision}
                type='button'
                onClick={() => handleSelectDecision(decision)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-white transition',
                  style.bg,
                  style.hover,
                )}>
                <Icon size={14} />
                {label}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Terminal state message */}
      {isTerminal && !selectedDecision ? (
        <p className='text-sm text-gray-500 italic'>{t('submissionDetail.decision.alreadyDecided')}</p>
      ) : null}

      {/* Decision form */}
      {selectedDecision && !isConfirming ? (
        <div className='space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4'>
          <div className='flex items-center gap-2'>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold',
                DECISION_BADGE_STYLES[selectedDecision],
              )}>
              {t(`submissionDetail.decision.${selectedDecision.toLowerCase()}`)}
            </span>
          </div>

          {/* Reason code - required for NO/WAITLIST */}
          {requiresReason ? (
            <div>
              <label htmlFor='decision-reason' className='block text-xs font-medium text-gray-600 mb-1'>
                {t('submissionDetail.decision.reasonCode')} *
              </label>
              <select
                id='decision-reason'
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                className={cn(
                  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                  'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
                )}>
                <option value=''>{t('submissionDetail.decision.reasonCodePlaceholder')}</option>
                {REASON_CODE_KEYS.map((code) => (
                  <option key={code} value={code}>
                    {t(`submissionDetail.decision.reasonCodes.${code}`)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Notes */}
          <div>
            <label htmlFor='decision-notes' className='block text-xs font-medium text-gray-600 mb-1'>
              {t('submissionDetail.decision.notes')}
            </label>
            <textarea
              id='decision-notes'
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('submissionDetail.decision.notesPlaceholder')}
              rows={3}
              className={cn(
                'w-full rounded-md border border-gray-300 px-3 py-2 text-sm resize-none',
                'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
              )}
            />
          </div>

          {/* Actions */}
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={handleConfirmClick}
              disabled={requiresReason && !reasonCode}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-white transition',
                DECISION_STYLES[selectedDecision].bg,
                DECISION_STYLES[selectedDecision].hover,
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}>
              {t('submissionDetail.decision.confirm')}
            </button>
            <button
              type='button'
              onClick={handleCancel}
              className='rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 transition'>
              {t('submissionDetail.decision.cancel')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Confirmation dialog */}
      {isConfirming ? (
        <div className='rounded-lg border-2 border-yellow-300 bg-yellow-50 p-4 space-y-3'>
          <h4 className='text-sm font-semibold text-gray-900'>{t('submissionDetail.decision.confirmTitle')}</h4>
          <p className='text-sm text-gray-700'>{confirmMessage}</p>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={() => void handleSubmit()}
              disabled={isSubmitting}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium text-white transition',
                DECISION_STYLES[selectedDecision!].bg,
                DECISION_STYLES[selectedDecision!].hover,
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}>
              {isSubmitting ? <Loader2 size={14} className='animate-spin' /> : <CheckCircle size={14} />}
              {isSubmitting ? t('common.loading') : t('submissionDetail.decision.confirm')}
            </button>
            <button
              type='button'
              onClick={() => setIsConfirming(false)}
              disabled={isSubmitting}
              className='rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 transition'>
              {t('submissionDetail.decision.cancel')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Decision history */}
      <div className='mt-4'>
        <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2'>
          {t('submissionDetail.decision.history')}
        </h4>

        {isLoadingHistory ? (
          <div className='flex items-center gap-2 text-sm text-gray-400'>
            <Loader2 size={14} className='animate-spin' />
            {t('common.loading')}
          </div>
        ) : null}

        {!isLoadingHistory && reviews.length === 0 ? (
          <p className='text-sm text-gray-400 italic'>{t('submissionDetail.decision.noDecisions')}</p>
        ) : null}

        {!isLoadingHistory && reviews.length > 0 ? (
          <div className='space-y-2'>
            {reviews.map((review) => (
              <div key={review.reviewId} className='rounded-md border border-gray-200 bg-white p-3 text-sm'>
                <div className='flex items-center gap-2 mb-1'>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
                      DECISION_BADGE_STYLES[review.decision] ?? 'bg-gray-100 text-gray-800',
                    )}>
                    {review.decision === 'YES' ? <ThumbsUp size={10} /> : null}
                    {review.decision === 'NO' ? <XCircle size={10} /> : null}
                    {review.decision === 'WAITLIST' ? <Clock size={10} /> : null}
                    {review.decision}
                  </span>
                  <span className='text-gray-500'>
                    {t('submissionDetail.decision.madeBy')} {review.reviewer.firstName} {review.reviewer.lastName}
                  </span>
                  <span className='text-gray-400 ml-auto'>{new Date(review.createdAt).toLocaleDateString()}</span>
                </div>
                {review.reasonCode ? (
                  <p className='text-xs text-gray-600'>
                    {t('submissionDetail.decision.reason')}:{' '}
                    {t(`submissionDetail.decision.reasonCodes.${review.reasonCode}`, review.reasonCode)}
                  </p>
                ) : null}
                {review.notes ? <p className='text-xs text-gray-500 mt-1'>{review.notes}</p> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
